import { logUsage } from 'owa-analytics-actions';
import { runAfterInitialRender } from 'owa-delayed-initialization';
import { getScriptBackupPath, getScriptPath } from 'owa-config';
import sleep from 'owa-sleep';
import { trace } from 'owa-trace';
import setPublicPath from './utils/setPublicPath';

export const MAX_ATTEMPTS = 5;
export const INITIAL_DELAY = 1000;

const SuccessAfterRetryDatapointName = 'LazyLoadSuccessAfterRetry';
const FailureDatapointName = 'LazyLoadFailure';

// For usage of this API, see the owa-bundling README
export default class LazyModule<TModule> {
    private promise: Promise<TModule> | undefined;
    private isLoaded = false;
    private attempts = 0;
    private pendingImports = 0;

    constructor(private importCallback: () => Promise<TModule>, private skipWait?: boolean) {}

    public import(): Promise<TModule> {
        // Initiate the import, if necessary
        if (!this.promise) {
            this.promise = new Promise<TModule>((resolve, reject) => {
                // Don't load any lazy resources until the initial render is complete
                (this.skipWait
                    ? func => {
                          func();
                      }
                    : runAfterInitialRender)(async () => {
                    // We'll retry several times if the load fails
                    this.attempts = 0;
                    while (!this.isLoaded && this.attempts < MAX_ATTEMPTS) {
                        try {
                            await this.loadModule(resolve);
                        } catch (error) {
                            await this.onLoadFailed(error, reject);
                        }
                    }
                });
            });
        }

        // Keep track of how many consumers are waiting for this import
        if (!this.isLoaded) {
            this.pendingImports++;
        }

        return this.promise;
    }

    private getUrl(attempt: number) {
        return MAX_ATTEMPTS - attempt <= 1 ? getScriptBackupPath() : getScriptPath();
    }

    private async loadModule(resolve: (value: TModule) => void) {
        let url = this.getUrl(this.attempts++);
        setPublicPath(url);
        const importPromise = this.importCallback();
        // we should set the public path after we are done to make sure any eager scripts are loaded from the main url
        setPublicPath(getScriptPath());
        resolve(await importPromise);

        this.isLoaded = true;
        this.pendingImports = 0;

        // Keep track of cases where retrying actually helps
        if (this.attempts > 1) {
            logUsage(SuccessAfterRetryDatapointName, {
                attempts: this.attempts.toString(),
                url,
            });
        }
    }

    private async onLoadFailed(error: any, reject: (reason: any) => void) {
        if (!error.request) {
            // When Weback fails to load a bundle it includes a 'request' property on the error; if
            // that property is absent then this must be an error that happened while evaluating
            // the module.
            error.scriptEval = true;
            trace.error(error);

            // The module is loaded but in a bad state (i.e. there's no point in trying to reload
            // it because it's already in Webpack's module cache).
            this.isLoaded = true;

            // We've already logged the error with trace.error; mark it as a known error so it
            // doesn't show up twice.
            error.knownError = true;
            reject(error);
        } else if (this.attempts >= MAX_ATTEMPTS) {
            // After MAX_ATTEMPTS, just fail
            logUsage(FailureDatapointName, {
                message: error.message,
                pendingImports: this.pendingImports,
            });

            // The error from webpack will always be different because it includes the chunk number
            // that failed; we create a new error so our analytics can bucket them together.
            let newError: any = new Error('Failed to load LazyModule');
            newError.knownError = true;
            reject(newError);
            this.pendingImports = 0;

            // Reset the cached promise so that we'll retry again the next time
            // import is attempted
            this.promise = undefined;
        } else if (this.attempts > 1) {
            // Delay before retrying
            await sleep(INITIAL_DELAY * Math.pow(2, this.attempts - 2));
        }
    }
}
